import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, Sprout, Sparkles, ArrowRight } from "lucide-react";

/**
 * Shown when the product catalog is temporarily empty.
 * Designed to feel intentional (not broken) so users still trust the brand.
 */
const EmptyProducts = () => {
  return (
    <div className="relative overflow-hidden mt-6" data-testid="empty-products">
      {/* Soft radial ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-background to-amber-50/40 dark:from-emerald-950/40 dark:via-background dark:to-amber-950/10" />
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-emerald-200/40 dark:bg-emerald-800/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-amber-200/40 dark:bg-amber-800/20 blur-3xl" />
      </div>

      <div className="max-w-3xl mx-auto text-center px-6 py-20 sm:py-28">
        {/* Growing sprout motif */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-white/70 dark:bg-white/5 backdrop-blur border border-emerald-200/60 dark:border-emerald-800/40 shadow-lg shadow-emerald-500/10"
        >
          <motion.div
            animate={{ rotate: [-4, 4, -4] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Sprout className="h-11 w-11 text-emerald-600 dark:text-emerald-400" />
          </motion.div>
        </motion.div>

        {/* Tagline pill */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-100/70 dark:bg-emerald-900/40 px-3 py-1 text-[11px] uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-300"
        >
          <Sparkles className="h-3 w-3" /> Coming soon
        </motion.div>

        {/* Main headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
        >
          We&rsquo;re curating{" "}
          <span className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-amber-500 bg-clip-text text-transparent">
            better choices
          </span>
          .
        </motion.h2>

        {/* Body */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed"
        >
          Our first collection of products is coming soon &mdash; each one evaluated
          through EcoWallet&rsquo;s sustainability intelligence and paired with rewards for
          making a better choice.
        </motion.p>

        {/* Signature line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-8 flex items-center justify-center gap-2 text-sm font-semibold tracking-wide"
        >
          <Leaf className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">Better choices.</span>
          <span className="h-1 w-1 rounded-full bg-emerald-500/60" />
          <span className="text-amber-700 dark:text-amber-300">Better rewards.</span>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.5 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <Link
            to="/for-brands"
            className="btn-pill bg-primary text-primary-foreground group"
            data-testid="empty-cta-brand"
          >
            Bring your brand to EcoWallet
            <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/about"
            className="btn-pill bg-muted text-foreground"
            data-testid="empty-cta-about"
          >
            How EcoWallet scores products
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default EmptyProducts;
